import _merge from 'lodash/merge';
import effectsTab from 'uploadcare-widget-tab-effects';
import uploadcare from 'uploadcare-widget';

import { getUploadAnalytics } from './analytics';
import { validateMinDimensions, validateMaxFileSize, validateFileType } from './validators';
import { patchUploadcareFileUrls, stripUploadcareUrlPatches } from './url';
import {
  uploadcareKey,
  DEFAULT_FILE_TYPES,
  DEFAULT_MAX_FILE_SIZE_MB,
  DEFAULT_TABS,
} from '../constants';
import { getWidgetCopy } from '../widgetCopy';

import thirdPartyTabStyles from '../thirdPartyTabStyles';

import '../UploadcareWidget.less';

const PROGRESS_STATE_CLASS = 'zola-state-progress';
const ERROR_STATE_CLASS = 'zola-state-error';

const getPreviewTab = showEffects => {
  if (showEffects) return effectsTab;

  let unwrappedUC;
  uploadcare.plugin(internalUC => {
    unwrappedUC = internalUC;
  });

  const { PreviewTab, PreviewTabMultiple } = unwrappedUC.widget.tabs;

  // The following function is based on https://github.com/uploadcare/uploadcare-widget/blob/1b33ac45986bffeb311bdb1293170b3925932aa1/src/widget/dialog.js#L215
  return (tabPanel, tabButton, dialogApi, settings, name) => {
    if (!settings.previewStep && dialogApi.fileColl.length() === 0) {
      return () => {};
    }
    const TabClass = settings.multiple ? PreviewTabMultiple : PreviewTab;
    return new TabClass(tabPanel, tabButton, dialogApi, settings, name);
  };
};

// Listens for changes to Uploadcare's file collection and adds an error or progress class
// to the dialog element as needed (depending on the files' status).
const handleWidgetState = (dialogElement, fileColl, maxFileCount) => {
  // eslint-disable-next-line no-underscore-dangle
  const widgetFiles = fileColl.__items;

  const updateWidgetState = () => {
    const anyFilesInProgress = widgetFiles.some(file => file.state() === 'pending');
    const anyFilesFailed = widgetFiles.some(file => file.state() === 'rejected');

    if (anyFilesInProgress) {
      dialogElement.addClass(PROGRESS_STATE_CLASS);
      dialogElement.removeClass(ERROR_STATE_CLASS);
    } else if (anyFilesFailed) {
      dialogElement.removeClass(PROGRESS_STATE_CLASS);
      dialogElement.addClass(ERROR_STATE_CLASS);
    } else {
      dialogElement.removeClass(PROGRESS_STATE_CLASS);
      dialogElement.removeClass(ERROR_STATE_CLASS);
    }
  };

  // User added a new file
  fileColl.onAdd.add(file => {
    // Immediately cancel the upload if over maxFileCount (to save time and bandwidth)
    if (maxFileCount && widgetFiles.length > maxFileCount) file.cancel();

    // When an upload starts, update the widget state
    updateWidgetState();

    // When an upload completes (success or failure), update the widget state
    file.always(updateWidgetState);
  });

  // User clicked file's trash icon
  fileColl.onRemove.add(file => {
    // When a pending file is removed from the list, force the upload to cancel (to save time and bandwidth)
    if (file.state() === 'pending') file.cancel();

    // When a file is removed, update the widget state
    file.always(updateWidgetState);
  });
};

export const openUploadcareModal = ({
  allowedFileTypes = DEFAULT_FILE_TYPES,
  closeViaOverlay = true,
  cropRatio,
  imageShrink,
  maxFileCount = 0,
  maxSize = DEFAULT_MAX_FILE_SIZE_MB,
  multipartMinSize,
  minFileCount = 1,
  minDimension,
  minHeight,
  minWidth,
  onAddPhotos,
  onUploadcareFileReady,
  showEffects = true,
  value,
  analyticsProduct,
  analyticsLocation,
  tabs = DEFAULT_TABS,
}) => {
  const isMultiple = minFileCount > 1 || maxFileCount > 1;

  // TODO: Restore analytics
  const { startAnalytics, stopAnalytics } = getUploadAnalytics(analyticsProduct, analyticsLocation);

  const handleFileSelect = file => {
    // If the cached image was deleted, ignore the change.
    if (!file) return;
    if (onAddPhotos) onAddPhotos();

    file.done(completedFile => {
      // Don't invoke callback if nothing changed
      const newUrl = stripUploadcareUrlPatches(completedFile.cdnUrl);
      const valueisIdentical = value && newUrl === value[0];
      if (valueisIdentical) return;

      onUploadcareFileReady(patchUploadcareFileUrls(completedFile));
    });
  };

  const handleFileGroupSelect = fileGroup => {
    // If all cached images were deleted, ignore the change.
    if (!fileGroup) return;
    if (onAddPhotos) onAddPhotos();

    const filePromises = fileGroup.files().map(file => {
      return new Promise(resolve => {
        file.done(completedFile => resolve(completedFile));
      });
    });

    Promise.all([fileGroup.promise(), ...filePromises]).then(
      ([completedGroup, ...completedFiles]) => {
        const newUrls = completedFiles.map(f => stripUploadcareUrlPatches(f.cdnUrl));
        const valuesAreIdentical = value && newUrls.every((newUrl, i) => newUrl === value[i]);

        // Only invoke callback if images or image order change.
        if (valuesAreIdentical) return;

        const patchedFiles = completedFiles.map(patchUploadcareFileUrls);
        onUploadcareFileReady(completedGroup.uuid, patchedFiles);
      }
    );
  };

  const validators = [
    validateMinDimensions(minWidth, minHeight, minDimension),
    validateMaxFileSize(maxSize),
    validateFileType(allowedFileTypes),
  ];

  const settings = {
    crop: cropRatio,
    customTabs: { preview: getPreviewTab(showEffects) },
    effect: `${cropRatio ? 'crop,' : ''}mirror,enhance,sharp,blur,grayscale`,
    imagesOnly: true,
    imageShrink,
    multiple: isMultiple,
    multipleMin: minFileCount,
    multipleMax: maxFileCount,
    multipartMinSize,
    publicKey: uploadcareKey,
    preferredTypes: allowedFileTypes.join(' '),
    preloader: null,
    previewStep: true,
    tabs,
    validators,
  };

  // Apply our custom styles to 3rd party tabs (e.g. Facebook, Dropbox)
  uploadcare.tabsCss.addStyle(thirdPartyTabStyles);

  // Make sure the dialog copy contains the current widget's settings
  uploadcare.plugin(internal => {
    const filledOutCopy = getWidgetCopy({
      minDimension,
      minWidth,
      minHeight,
      maxSize,
      maxFileCount,
      allowedFileTypes,
    });

    // eslint-disable-next-line no-param-reassign
    internal.locale.translations.en = _merge(internal.locale.translations.en, filledOutCopy);
    internal.locale.rebuild();
  });

  // Open the actual modal
  const { dialogElement, fileColl, always, addFiles, switchTab, done } = uploadcare.openDialog(
    null,
    settings
  );

  // Listen to file changes and apply the "error" or "in progress" classes to the widget modal as needed
  handleWidgetState(dialogElement, fileColl, maxFileCount);

  // Stop mousedown events from propagating outside of the dialog
  dialogElement.bind('mousedown', e => {
    e.stopPropagation();
  });

  // Sometimes, we want to make sure clients don't accidentally close the widget.
  if (!closeViaOverlay) {
    dialogElement.off('dblclick');
  }

  // Track file errors and successful uploads
  startAnalytics(fileColl);

  // When the dialog is closed and valid files are selected, invoke the callback
  done(isMultiple ? handleFileGroupSelect : handleFileSelect);

  // When the dialog is closed, make sure the analytics are stopped
  always(() => {
    stopAnalytics();
  });

  // If any existing files were provided, we need to add them to the dialog AFTER it's open.
  // This is because using uploadcare.filesFrom BEFORE the dialog creates a fileColl means that
  // fileColl.onAdd is never triggered, which breaks our progress / error detection in handleWidgetState.
  if (value) {
    let files;
    if (value instanceof FileList) {
      files = uploadcare.filesFrom('object', value, settings);
    } else {
      const urls = Array.isArray(value) ? value : [value]; // If value isn't already an array, create one
      const validUrls = urls.filter(url => !!url);
      const strippedUrls = validUrls.map(stripUploadcareUrlPatches); // Makes sure that the input and output URLs are formatted the same.
      files = strippedUrls.length ? strippedUrls : null;
    }

    addFiles(files);
    switchTab('preview');
  }
};
